import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Bookmark, BookmarkCheck, NotebookPen, MessageSquare, Send, Loader2, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Props {
  lessonId: string;
  userId: string;
  userName: string;
}

interface QaItem {
  id: string;
  user_id: string;
  author_name: string;
  is_admin_reply: boolean;
  content: string;
  created_at: string;
  parent_id: string | null;
}

const LessonExtras = ({ lessonId, userId, userName }: Props) => {
  const { toast } = useToast();
  const [bookmarked, setBookmarked] = useState(false);
  const [note, setNote] = useState("");
  const [savedNote, setSavedNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [qa, setQa] = useState<QaItem[]>([]);
  const [newQ, setNewQ] = useState("");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [b, n, ownQ] = await Promise.all([
        (supabase as any).from("lesson_bookmarks").select("id").eq("lesson_id", lessonId).eq("user_id", userId).maybeSingle(),
        (supabase as any).from("lesson_notes").select("content").eq("lesson_id", lessonId).eq("user_id", userId).maybeSingle(),
        // Only this student's own questions for this lesson
        (supabase as any).from("lesson_qa").select("*").eq("lesson_id", lessonId).eq("user_id", userId).is("parent_id", null).order("created_at", { ascending: true }),
      ]);
      setBookmarked(!!b.data);
      const noteContent = n.data?.content || "";
      setNote(noteContent);
      setSavedNote(noteContent);

      const myQuestions = (ownQ.data as QaItem[]) || [];
      // Fetch admin replies to those questions
      let replies: QaItem[] = [];
      if (myQuestions.length > 0) {
        const ids = myQuestions.map((q) => q.id);
        const { data: rep } = await (supabase as any)
          .from("lesson_qa")
          .select("*")
          .in("parent_id", ids)
          .order("created_at", { ascending: true });
        replies = (rep as QaItem[]) || [];
      }
      // Merge & sort chronologically
      const merged = [...myQuestions, ...replies].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );
      setQa(merged);
    };
    load();

    // Realtime: pick up admin replies to this user's questions instantly.
    // RLS ensures only the user's own questions and admin replies to them stream through.
    const channel = (supabase as any)
      .channel(`lesson-qa-${lessonId}-${userId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "lesson_qa", filter: `lesson_id=eq.${lessonId}` },
        (payload: any) => {
          const row = payload.new as QaItem;
          // Defense in depth: client-side filter on top of RLS
          setQa((prev) => {
            if (prev.some((q) => q.id === row.id)) return prev;
            const myIds = new Set(prev.filter((q) => q.user_id === userId && !q.parent_id).map((q) => q.id));
            const isOwn = row.user_id === userId && !row.parent_id;
            const isReplyToMe = row.parent_id && myIds.has(row.parent_id);
            if (isOwn || isReplyToMe) {
              return [...prev, row].sort(
                (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
              );
            }
            return prev;
          });
        },
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "lesson_qa", filter: `lesson_id=eq.${lessonId}` },
        (payload: any) => {
          // REPLICA IDENTITY FULL on lesson_qa means payload.old contains the full row.
          const oldRow = payload.old as Partial<QaItem> | undefined;
          const removedId = oldRow?.id;
          const removedParentId = oldRow?.parent_id ?? null;
          if (!removedId) return;
          // Drop the deleted row AND any replies whose parent was deleted (cascade safety).
          setQa((prev) =>
            prev.filter(
              (q) => q.id !== removedId && (!removedParentId ? q.parent_id !== removedId : true),
            ),
          );
        },
      )
      .subscribe((status: string, err?: Error) => {
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          console.error("Q&A realtime subscription failed:", status, err);
          toast({
            title: "Live updates unavailable",
            description: "We can't stream new replies right now. Refresh to see updates.",
            variant: "destructive",
          });
        }
      });

    return () => {
      (supabase as any).removeChannel(channel);
    };
  }, [lessonId, userId, toast]);

  const toggleBookmark = async () => {
    if (bookmarked) {
      await (supabase as any).from("lesson_bookmarks").delete().eq("user_id", userId).eq("lesson_id", lessonId);
      setBookmarked(false);
    } else {
      await (supabase as any).from("lesson_bookmarks").insert({ user_id: userId, lesson_id: lessonId });
      setBookmarked(true);
    }
  };

  const saveNote = async () => {
    setSavingNote(true);
    try {
      const { error } = await (supabase as any).from("lesson_notes").upsert(
        { user_id: userId, lesson_id: lessonId, content: note },
        { onConflict: "user_id,lesson_id" }
      );
      if (error) {
        toast({ title: "Couldn't save note", description: error.message, variant: "destructive" });
        return;
      }
      setSavedNote(note);
      toast({ title: "Note saved" });
    } finally {
      setSavingNote(false);
    }
  };

  const postQuestion = async () => {
    if (!newQ.trim()) return;
    setPosting(true);
    try {
      const questionText = newQ.trim();
      const { data, error } = await (supabase as any)
        .from("lesson_qa")
        .insert({
          lesson_id: lessonId,
          user_id: userId,
          author_name: userName,
          content: questionText,
          parent_id: null,
          is_admin_reply: false,
        })
        .select()
        .single();
      if (error) {
        toast({
          title: "Couldn't post question",
          description: error.message,
          variant: "destructive",
        });
        return;
      }
      if (data) setQa((p) => (p.some((q) => q.id === data.id) ? p : [...p, data as QaItem]));
      setNewQ("");
      // Notify admins via Telegram with an inline Reply button (best-effort)
      try {
        const { data: lesson } = await (supabase as any)
          .from("lessons")
          .select("title, courses(title)")
          .eq("id", lessonId)
          .maybeSingle();
        const lessonTitle = lesson?.title ?? "a lesson";
        const courseTitle = lesson?.courses?.title ?? "";
        await supabase.functions.invoke("telegram-notify", {
          body: {
            target: "admins",
            text: `❓ <b>New Q&amp;A from ${userName}</b>\n${courseTitle ? `📚 ${courseTitle}\n` : ""}📝 ${lessonTitle}\n\n${questionText}`,
            reply_markup: data?.id
              ? {
                  inline_keyboard: [[
                    { text: "↩️ Reply", callback_data: `qa:reply:${data.id}:0` },
                    { text: "📋 Open Q&A", callback_data: "m:qa:0" },
                  ]],
                }
              : undefined,
          },
        });
      } catch (e) {
        console.error("Notify admins failed", e);
      }
      toast({ title: "Question posted", description: "An admin has been notified." });
    } catch (e: any) {
      toast({ title: "Couldn't post question", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setPosting(false);
    }
  };

  const deleteQ = async (id: string) => {
    await (supabase as any).from("lesson_qa").delete().eq("id", id);
    setQa((p) => p.filter((q) => q.id !== id));
  };

  const noteDirty = note !== savedNote;

  return (
    <div className="space-y-4">
      {/* Bookmark + Notes */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm flex items-center gap-2">
            <NotebookPen className="w-4 h-4" /> My Notes
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={toggleBookmark}>
            {bookmarked ? (
              <BookmarkCheck className="w-4 h-4 text-primary fill-primary" />
            ) : (
              <Bookmark className="w-4 h-4" />
            )}
            <span className="ml-1 text-xs">{bookmarked ? "Bookmarked" : "Bookmark"}</span>
          </Button>
        </CardHeader>
        <CardContent className="pt-0 space-y-2">
          <Textarea
            placeholder="Write notes for this lesson..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="resize-none"
          />
          {noteDirty && (
            <Button size="sm" onClick={saveNote} disabled={savingNote}>
              {savingNote && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Save note
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Q&A */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> Questions & Answers ({qa.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          {qa.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">No questions yet. Be the first to ask!</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {qa.map((q) => (
                <div
                  key={q.id}
                  className={`p-3 rounded-md border text-sm ${
                    q.is_admin_reply ? "bg-primary/5 border-primary/30" : "bg-muted/30 border-border"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold">
                      {q.author_name}
                      {q.is_admin_reply && <span className="ml-1 text-primary">• Admin</span>}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {new Date(q.created_at).toLocaleDateString()}
                      </span>
                      {q.user_id === userId && (
                        <button onClick={() => deleteQ(q.id)} className="text-muted-foreground hover:text-destructive" aria-label="Delete">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="whitespace-pre-wrap">{q.content}</p>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Textarea
              placeholder="Ask a question about this lesson..."
              value={newQ}
              onChange={(e) => setNewQ(e.target.value)}
              rows={2}
              className="resize-none flex-1"
            />
            <Button size="sm" onClick={postQuestion} disabled={posting || !newQ.trim()} className="self-end">
              {posting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LessonExtras;
